import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Download, Filter, TrendingUp, TrendingDown, Minus, Calendar, User, Building2, FileSpreadsheet, FileText } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useToast } from '../components/Toast';
import { getAppointments } from '../services/appointmentApi';
import { searchEmployees } from '../services/employeeApi';
import { searchClients } from '../services/clientApi';
import DateInput from '../components/DateInput';
import Pagination from '../components/Pagination';
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils';

interface DiscrepancyItem {
  id: string;
  client_name: string;
  property_name: string;
  employee_name: string;
  service_type: string;
  scheduled_date: string;
  duration: number; // Tempo estimado em minutos
  actual_duration: number; // Tempo real em minutos
  difference: number; // Diferença em minutos
  differencePercent: number; // Diferença percentual
  status: 'over' | 'under' | 'exact';
}

interface FilterState {
  startDate: string;
  endDate: string;
  employeeId: string;
  clientId: string;
}

const TimeDiscrepanciesReport: React.FC = () => {
  const { t, formatDate } = useI18n();
  const { showError, showSuccess } = useToast();
  const [discrepancies, setDiscrepancies] = useState<DiscrepancyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    employeeId: '',
    clientId: '',
  });
  const [employees, setEmployees] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(true);

  // Carregar funcionários e clientes para filtros
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [employeesData, clientsData] = await Promise.all([
          searchEmployees(''),
          searchClients(''),
        ]);
        setEmployees(Array.isArray(employeesData) ? employeesData : []);
        setClients(Array.isArray(clientsData) ? clientsData : []);
      } catch (error) {
        console.error('Erro ao carregar opções de filtro:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Carregar discrepâncias
  useEffect(() => {
    loadDiscrepancies();
  }, [filters]);

  const loadDiscrepancies = async () => {
    setIsLoading(true);
    try {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);

      // Buscar appointments completados no período
      const params: any = {
        status: 'completed',
      };

      if (filters.employeeId) {
        params.employee_id = filters.employeeId;
      }

      if (filters.clientId) {
        params.client_id = filters.clientId;
      }

      const appointments = await getAppointments(params);
      const appointmentsArray = Array.isArray(appointments)
        ? appointments
        : appointments?.appointments || appointments?.data || [];

      // Filtrar por período e calcular discrepâncias
      const discrepanciesList: DiscrepancyItem[] = [];

      appointmentsArray.forEach((apt: any) => {
        const aptDate = new Date(apt.scheduled_date || apt.completed_at || apt.end_time);
        if (aptDate < startDate || aptDate > endDate) return;

        const duration = apt.duration || 120;
        const actualDuration = apt.actual_duration;

        if (!actualDuration) return; // Só incluir se tiver tempo real

        const difference = actualDuration - duration;
        const differencePercent = duration > 0 ? Math.round((difference / duration) * 100) : 0;

        let status: 'over' | 'under' | 'exact' = 'exact';
        if (difference > 0) {
          status = 'over';
        } else if (difference < 0) {
          status = 'under';
        }

        discrepanciesList.push({
          id: apt.id || '',
          client_name: apt.client_name || 'Cliente não informado',
          property_name: apt.property_name || 'Propriedade não informada',
          employee_name: apt.employee_name || 'Funcionário não informado',
          service_type: apt.service_type || 'limpeza',
          scheduled_date: apt.scheduled_date || apt.completed_at || '',
          duration,
          actual_duration: actualDuration,
          difference,
          differencePercent,
          status,
        });
      });

      // Ordenar por diferença absoluta (maiores discrepâncias primeiro)
      discrepanciesList.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));

      setDiscrepancies(discrepanciesList);
      setCurrentPage(1); // Reset para primeira página
    } catch (error: any) {
      showError('Erro ao carregar discrepâncias de tempo');
      console.error('Erro:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Estatísticas agregadas
  const stats = useMemo(() => {
    if (discrepancies.length === 0) {
      return {
        total: 0,
        overTime: 0,
        underTime: 0,
        exactTime: 0,
        averageDifference: 0,
        maxOverTime: 0,
        maxUnderTime: 0,
      };
    }

    const overTime = discrepancies.filter((d) => d.status === 'over').length;
    const underTime = discrepancies.filter((d) => d.status === 'under').length;
    const exactTime = discrepancies.filter((d) => d.status === 'exact').length;
    const averageDifference =
      discrepancies.reduce((sum, d) => sum + d.difference, 0) / discrepancies.length;
    const maxOverTime = Math.max(
      ...discrepancies.filter((d) => d.status === 'over').map((d) => d.difference),
      0
    );
    const maxUnderTime = Math.min(
      ...discrepancies.filter((d) => d.status === 'under').map((d) => d.difference),
      0
    );

    return {
      total: discrepancies.length,
      overTime,
      underTime,
      exactTime,
      averageDifference: Math.round(averageDifference * 10) / 10,
      maxOverTime,
      maxUnderTime,
    };
  }, [discrepancies]);

  // Paginação
  const paginatedDiscrepancies = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return discrepancies.slice(start, end);
  }, [discrepancies, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(discrepancies.length / itemsPerPage);

  // Exportar relatório em diferentes formatos
  const exportReport = (format: 'csv' | 'excel' | 'pdf' = 'csv') => {
    if (discrepancies.length === 0) {
      showError('Não há dados para exportar');
      return;
    }

    const headers = ['Cliente', 'Propriedade', 'Funcionário', 'Tipo de Serviço', 'Data', 'Tempo Estimado (min)', 'Tempo Real (min)', 'Diferença (min)', 'Diferença (%)'];
    const data = discrepancies.map((d) => ({
      'Cliente': d.client_name,
      'Propriedade': d.property_name,
      'Funcionário': d.employee_name,
      'Tipo de Serviço': d.service_type,
      'Data': formatDate(d.scheduled_date),
      'Tempo Estimado (min)': d.duration,
      'Tempo Real (min)': d.actual_duration,
      'Diferença (min)': d.difference,
      'Diferença (%)': d.differencePercent,
    }));

    const filename = `discrepancias_tempo_${new Date().toISOString().split('T')[0]}`;

    try {
      switch (format) {
        case 'csv':
          exportToCSV(data, filename, headers);
          showSuccess('Relatório CSV exportado com sucesso!');
          break;
        case 'excel':
          exportToExcel(data, filename, headers, 'Discrepâncias de Tempo');
          showSuccess('Relatório Excel exportado com sucesso!');
          break;
        case 'pdf':
          // Criar HTML para PDF
          const htmlTable = `
            <table>
              <thead>
                <tr>
                  ${headers.map(h => `<th>${h}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${data.map(row => `
                  <tr>
                    ${headers.map(h => `<td>${row[h] || ''}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `;
          exportToPDF('Relatório de Discrepâncias de Tempo', htmlTable, filename);
          showSuccess('Relatório PDF exportado com sucesso!');
          break;
      }
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      showError('Erro ao exportar relatório');
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(Math.abs(minutes) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes < 0 ? '-' : '+';
    if (hours > 0) {
      return `${sign}${hours}h ${mins}min`;
    }
    return `${sign}${mins}min`;
  };

  // Preparar dados para gráfico de tendência (agrupar por data)
  const trendData = useMemo(() => {
    const grouped: Record<string, { over: number; under: number; exact: number; total: number }> = {};
    
    discrepancies.forEach((item) => {
      const date = formatDate(item.scheduled_date);
      if (!grouped[date]) {
        grouped[date] = { over: 0, under: 0, exact: 0, total: 0 };
      }
      grouped[date][item.status]++;
      grouped[date].total++;
    });

    return Object.entries(grouped)
      .map(([date, counts]) => ({ date, ...counts }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [discrepancies, formatDate]);

  return (
    <div style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>Relatório de Discrepâncias de Tempo</h1>
          <p style={{ color: '#666', margin: 0 }}>{t('timeDiscrepancies.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
          <button
            onClick={() => exportReport('csv')}
            disabled={discrepancies.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: discrepancies.length === 0 ? 'not-allowed' : 'pointer',
              opacity: discrepancies.length === 0 ? 0.5 : 1,
            }}
          >
            <Download size={18} />
            CSV
          </button>
          <button
            onClick={() => exportReport('excel')}
            disabled={discrepancies.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: discrepancies.length === 0 ? 'not-allowed' : 'pointer',
              opacity: discrepancies.length === 0 ? 0.5 : 1,
            }}
          >
            <FileSpreadsheet size={18} />
            Excel
          </button>
          <button
            onClick={() => exportReport('pdf')}
            disabled={discrepancies.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: discrepancies.length === 0 ? 'not-allowed' : 'pointer',
              opacity: discrepancies.length === 0 ? 0.5 : 1,
            }}
          >
            <FileText size={18} />
            PDF
          </button>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '30px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={20} />
              Filtros
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Ocultar
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px',
            }}
          >
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Data Inicial
              </label>
              <DateInput
                value={filters.startDate}
                onChange={(date) => setFilters({ ...filters, startDate: date })}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Data Final
              </label>
              <DateInput
                value={filters.endDate}
                onChange={(date) => setFilters({ ...filters, endDate: date })}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Funcionário
              </label>
              <select
                value={filters.employeeId}
                onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('common.all')}</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Cliente
              </label>
              <select
                value={filters.clientId}
                onChange={(e) => setFilters({ ...filters, clientId: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('common.all')}</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {!showFilters && (
        <button
          onClick={() => setShowFilters(true)}
          style={{
            marginBottom: '20px',
            padding: '8px 16px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #ddd',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Filter size={16} />
          Mostrar Filtros
        </button>
      )}

      {/* Estatísticas */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #3b82f6',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <Clock style={{ fontSize: '24px', color: '#3b82f6', marginRight: '10px' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{t('common.total')}</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>
            {isLoading ? '...' : stats.total}
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #dc2626',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <TrendingUp style={{ fontSize: '24px', color: '#dc2626', marginRight: '10px' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Excederam Tempo</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626' }}>
            {isLoading ? '...' : stats.overTime}
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #10b981',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <TrendingDown style={{ fontSize: '24px', color: '#10b981', marginRight: '10px' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Mais Rápidos</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>
            {isLoading ? '...' : stats.underTime}
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #6b7280',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <Minus style={{ fontSize: '24px', color: '#6b7280', marginRight: '10px' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>No Tempo Exato</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#6b7280' }}>
            {isLoading ? '...' : stats.exactTime}
          </div>
        </div>

        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #f59e0b',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <Clock style={{ fontSize: '24px', color: '#f59e0b', marginRight: '10px' }} />
            <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>{t('timeDiscrepancies.avgDifference')}</h3>
          </div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>
            {isLoading ? '...' : formatTime(stats.averageDifference)}
          </div>
        </div>
      </div>

      {/* Gráficos de Tendência */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ margin: '0 0 20px 0', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <TrendingUp size={20} />
          Gráficos de Tendência
        </h3>
        {discrepancies.length > 0 ? (
          <TrendChart data={trendData} />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            Não há dados suficientes para exibir gráficos.
          </div>
        )}
      </div>

      {/* Lista de Discrepâncias */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Lista Detalhada</h3>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>{t('common.loading')}</div>
        ) : paginatedDiscrepancies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            Nenhuma discrepância encontrada no período selecionado.
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>{t('common.client')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>{t('common.property')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>{t('common.employee')}</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>{t('common.date')}</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Estimado</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Real</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Diferença</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDiscrepancies.map((item) => (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={{ padding: '12px', color: '#374151' }}>{item.client_name}</td>
                      <td style={{ padding: '12px', color: '#374151' }}>{item.property_name}</td>
                      <td style={{ padding: '12px', color: '#374151' }}>{item.employee_name}</td>
                      <td style={{ padding: '12px', color: '#374151' }}>{formatDate(item.scheduled_date)}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#374151' }}>{item.duration} min</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#374151' }}>{item.actual_duration} min</td>
                      <td
                        style={{
                          padding: '12px',
                          textAlign: 'right',
                          fontWeight: '600',
                          color: item.status === 'over' ? '#dc2626' : item.status === 'under' ? '#10b981' : '#6b7280',
                        }}
                      >
                        {formatTime(item.difference)}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          textAlign: 'right',
                          fontWeight: '600',
                          color: item.status === 'over' ? '#dc2626' : item.status === 'under' ? '#10b981' : '#6b7280',
                        }}
                      >
                        {item.differencePercent > 0 ? '+' : ''}
                        {item.differencePercent}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={discrepancies.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                showItemsPerPage={true}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Componente de Gráfico de Tendência
interface TrendChartProps {
  data: Array<{ date: string; over: number; under: number; exact: number; total: number }>;
}

const TrendChart: React.FC<TrendChartProps> = ({ data }) => {
  if (data.length === 0) return null;

  const maxValue = Math.max(...data.map((d) => d.total), 1);
  const chartHeight = 300;
  const chartWidth = Math.max(800, data.length * 60);
  const padding = { top: 20, right: 20, bottom: 40, left: 60 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  const getX = (index: number) => padding.left + (index / (data.length - 1 || 1)) * innerWidth;
  const getY = (value: number) => padding.top + innerHeight - (value / maxValue) * innerHeight;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={chartWidth} height={chartHeight} style={{ display: 'block' }}>
        {/* Eixos */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + innerHeight}
          stroke="#e5e7eb"
          strokeWidth="2"
        />
        <line
          x1={padding.left}
          y1={padding.top + innerHeight}
          x2={padding.left + innerWidth}
          y2={padding.top + innerHeight}
          stroke="#e5e7eb"
          strokeWidth="2"
        />

        {/* Linhas de grade */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + innerHeight - ratio * innerHeight;
          return (
            <line
              key={ratio}
              x1={padding.left}
              y1={y}
              x2={padding.left + innerWidth}
              y2={y}
              stroke="#f3f4f6"
              strokeWidth="1"
              strokeDasharray="4 4"
            />
          );
        })}

        {/* Labels do eixo Y */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const value = Math.round(ratio * maxValue);
          const y = padding.top + innerHeight - ratio * innerHeight;
          return (
            <text
              key={ratio}
              x={padding.left - 10}
              y={y + 4}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              {value}
            </text>
          );
        })}

        {/* Barras empilhadas */}
        {data.map((item, index) => {
          const x = getX(index);
          const barWidth = innerWidth / data.length * 0.8;
          const barX = x - barWidth / 2;
          
          let currentY = padding.top + innerHeight;
          
          // Barra de "exact" (cinza)
          const exactHeight = (item.exact / maxValue) * innerHeight;
          currentY -= exactHeight;
          
          // Barra de "under" (verde)
          const underHeight = (item.under / maxValue) * innerHeight;
          const underY = currentY - underHeight;
          
          // Barra de "over" (vermelho)
          const overHeight = (item.over / maxValue) * innerHeight;
          const overY = underY - overHeight;

          return (
            <g key={item.date}>
              {/* Exact */}
              {exactHeight > 0 && (
                <rect
                  x={barX}
                  y={currentY}
                  width={barWidth}
                  height={exactHeight}
                  fill="#6b7280"
                  opacity={0.7}
                />
              )}
              {/* Under */}
              {underHeight > 0 && (
                <rect
                  x={barX}
                  y={underY}
                  width={barWidth}
                  height={underHeight}
                  fill="#10b981"
                  opacity={0.7}
                />
              )}
              {/* Over */}
              {overHeight > 0 && (
                <rect
                  x={barX}
                  y={overY}
                  width={barWidth}
                  height={overHeight}
                  fill="#dc2626"
                  opacity={0.7}
                />
              )}
              
              {/* Label da data */}
              <text
                x={x}
                y={padding.top + innerHeight + 20}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7280"
                transform={`rotate(-45 ${x} ${padding.top + innerHeight + 20})`}
              >
                {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </text>
            </g>
          );
        })}

        {/* Legenda */}
        <g transform={`translate(${padding.left + innerWidth - 150}, ${padding.top})`}>
          <rect x={0} y={0} width="12" height="12" fill="#dc2626" opacity={0.7} />
          <text x={15} y={10} fontSize="12" fill="#374151">Excederam</text>
          <rect x={0} y={20} width="12" height="12" fill="#10b981" opacity={0.7} />
          <text x={15} y={30} fontSize="12" fill="#374151">Mais Rápidos</text>
          <rect x={0} y={40} width="12" height="12" fill="#6b7280" opacity={0.7} />
          <text x={15} y={50} fontSize="12" fill="#374151">No Tempo</text>
        </g>
      </svg>
    </div>
  );
};

export default TimeDiscrepanciesReport;

